import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useIsAdmin, useIsAccountant } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard,
  Package,
  Users,
  Building2,
  ShoppingCart,
  FileText,
  Warehouse,
  BarChart3,
  LogOut,
  Menu,
  X,
  Settings,
  RotateCcw,
  Eye,
  ClipboardList,
  FileSpreadsheet,
  Zap,
  Link2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Full nav items for Admin
const adminNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Package, label: 'Products', path: '/products' },
  { icon: Users, label: 'Buyer Items', path: '/buyer-items' },
  { icon: Link2, label: 'System Mappings', path: '/system-mappings' },
  { icon: FileText, label: 'Sales Quotations', path: '/sales-quotations' },
  { icon: Building2, label: 'Vendors', path: '/vendors' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: FileSpreadsheet, label: 'Quotations', path: '/quotations' },
  { icon: ClipboardList, label: 'Purchase Orders', path: '/purchase-orders' },
  { icon: ShoppingCart, label: 'Purchases', path: '/purchases' },
  { icon: FileText, label: 'Invoices', path: '/sales' },
  { icon: Zap, label: 'Quick GST Invoice', path: '/quick-invoices' },
  { icon: RotateCcw, label: 'Credit Notes', path: '/credit-notes' },
  { icon: Warehouse, label: 'Inventory', path: '/inventory' },
  { icon: BarChart3, label: 'Reports', path: '/reports' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

// Simplified nav items for Accountant (view-only access)
const accountantNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: Building2, label: 'Vendors', path: '/vendors' },
  { icon: FileSpreadsheet, label: 'Quotations', path: '/quotations' },
  { icon: ClipboardList, label: 'Purchase Orders', path: '/purchase-orders' },
  { icon: ShoppingCart, label: 'Purchases', path: '/purchases' },
  { icon: FileText, label: 'Invoices', path: '/sales' },
  { icon: Zap, label: 'Quick GST Invoice', path: '/quick-invoices' },
  { icon: RotateCcw, label: 'Credit Notes', path: '/credit-notes' },
  { icon: BarChart3, label: 'Reports', path: '/reports' },
];

// Default branding config
const defaultBranding = {
  company_name: 'Nirala Ennem Enterprises',
  system_name: 'GST Invoice & Inventory Management System',
  logo_url: '/assets/logo.png',
  primary_color: '#0d9488',
  footer_left: '© 2026 Nirala Ennem Enterprises',
  footer_right: 'GST Invoice & Inventory Management System'
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState(defaultBranding);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = useIsAdmin();
  const isAccountant = useIsAccountant();

  // Select nav items based on role
  const navItems = isAccountant ? accountantNavItems : adminNavItems;

  // Fetch branding settings
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings/branding`);
        if (res.data) {
          setBranding({ ...defaultBranding, ...res.data });
        }
      } catch (error) {
        // Use default branding if fetch fails
        console.log('Using default branding');
      }
    };
    fetchBranding();
  }, []);

  // Poll the "Buyer Items pending assignment" counter used by the sidebar badge.
  // Refreshes on mount, when the user navigates, and whenever any Buyer-Item
  // mutation fires the `buyer-items-updated` window event.
  useEffect(() => {
    if (isAccountant) return; // sidebar badge is admin-only (endpoint is admin-only too)
    let alive = true;
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API_URL}/buyer-items/pending-assignment-count`);
        if (alive) setPendingCount(res.data?.count || 0);
      } catch (_e) {
        if (alive) setPendingCount(0);
      }
    };
    fetchPending();
    const onUpdated = () => fetchPending();
    window.addEventListener('buyer-items-updated', onUpdated);
    return () => { alive = false; window.removeEventListener('buyer-items-updated', onUpdated); };
  }, [isAccountant, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get current page title
  const getCurrentPageTitle = () => {
    const currentNav = navItems.find(item => item.path === location.pathname);
    return currentNav?.label || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <img 
              src={branding.logo_url} 
              alt={branding.company_name} 
              className="h-8 w-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="hidden xs:block">
              <span className="font-semibold text-slate-900 text-sm">Nirala ERP</span>
            </div>
          </div>
        </div>
        <span className="text-sm font-medium text-slate-600">{getCurrentPageTitle()}</span>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-navy-900 border-r border-navy-950 z-50 transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Branding Header */}
          <div className="h-auto py-4 px-4 border-b border-navy-800">
            <div className="flex items-center gap-3">
              <img 
                src={branding.logo_url}
                alt={branding.company_name}
                className="h-10 w-auto flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="min-w-0">
                <h1 className="font-semibold text-white text-base tracking-tight leading-tight">
                  Nirala Ennem ERP
                </h1>
                <p className="text-[10px] text-navy-200/70 font-medium leading-tight mt-0.5">
                  {branding.system_name?.split(' - ')[0] || 'GST Invoice & Inventory'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-0.5">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                      isActive
                        ? "bg-teal-500/15 text-teal-100 border-l-2 border-teal-400 pl-[10px]"
                        : "text-navy-100/80 hover:bg-navy-800/60 hover:text-white"
                    )}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon 
                      className={cn("w-[18px] h-[18px]", isActive ? "text-teal-300" : "text-navy-200/60")} 
                      strokeWidth={1.75} 
                    />
                    <span className="flex-1">{item.label}</span>
                    {item.path === '/buyer-items' && pendingCount > 0 && (
                      <Badge
                        className="bg-teal-500/20 text-teal-100 hover:bg-teal-500/20 border-0 text-[10px] tabular-nums px-1.5 py-0 h-4 leading-none"
                        data-testid="nav-buyer-items-badge"
                        title={`${pendingCount} product(s) have exactly one unique buyer item match awaiting assignment`}
                      >
                        {pendingCount}
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-navy-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-teal-500/20 rounded-full flex items-center justify-center ring-1 ring-teal-400/30">
                <span className="text-sm font-semibold text-teal-100">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
                <div className="flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-[10px] px-1.5 py-0 border-0",
                      isAccountant 
                        ? "bg-navy-700/60 text-navy-100" 
                        : "bg-teal-500/20 text-teal-100"
                    )}
                  >
                    {isAccountant ? (
                      <>
                        <Eye className="w-2.5 h-2.5 mr-1" />
                        View Only
                      </>
                    ) : (
                      'Admin'
                    )}
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 bg-transparent border-navy-700 text-navy-100/90 hover:bg-navy-800 hover:text-white hover:border-navy-600 h-9 text-sm"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 flex-1 flex flex-col">
        {/* Desktop Top Bar */}
        <div className="hidden lg:flex h-14 bg-white border-b border-slate-200 items-center justify-between px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-semibold text-slate-800 tracking-tight">{getCurrentPageTitle()}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 border border-slate-200 rounded-md">
              <img 
                src={branding.logo_url}
                alt={branding.company_name}
                className="h-5 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-xs font-medium text-slate-600">Nirala Ennem ERP</span>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full flex-1">
          {children}
        </div>

        {/* Professional Footer */}
        <footer className="lg:pl-0 bg-[#f8f9fa] border-t border-slate-200 mt-auto">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-slate-500">
              <span>{branding.footer_left}</span>
              <span className="hidden sm:inline text-slate-300">|</span>
              <span>{branding.footer_right}</span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
